const MAX_SIZE = 6 * 1024 * 1024; // 6MB
const MAX_DIMENSION = 4096;
const INITIAL_QUALITY = 0.92;
const QUALITY_STEP = 0.1;
const MIN_QUALITY = 0.52;
const HEIC_EXTENSION = /\.(heic|heif)$/i;
const HEIC_BRANDS = [
  "heic",
  "heix",
  "heim",
  "heis",
  "hevc",
  "hevx",
  "hevm",
  "hevs",
  "mif1",
  "msf1",
];

export class HeicConversionError extends Error {}

async function compressToJpeg(bitmap, maxWidth, maxHeight, quality) {
  const canvas = new OffscreenCanvas(maxWidth, maxHeight);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, maxWidth, maxHeight);
  return canvas.convertToBlob({ type: "image/jpeg", quality });
}

function scaledDimensions(width, height, maxDimension) {
  if (width <= maxDimension && height <= maxDimension) return { width, height };

  const ratio = Math.min(maxDimension / width, maxDimension / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function jpegFileName(originalName) {
  const base = originalName.replace(/\.[^.]+$/, "");
  return `${base}.jpg`;
}

async function isHeic(file) {
  if (/^image\/hei[cf]/.test(file.type) || HEIC_EXTENSION.test(file.name)) return true;

  const header = new Uint8Array(await file.slice(4, 12).arrayBuffer());
  const box = String.fromCharCode(...header);
  return box.startsWith("ftyp") && HEIC_BRANDS.includes(box.slice(4));
}

let heicDecoder;

function loadHeicDecoder(url) {
  heicDecoder ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.onload = () => resolve(window.HeicTo);
    script.onerror = () => {
      heicDecoder = null;
      script.remove();
      reject(new Error(`Couldn't load ${url}`));
    };
    document.head.append(script);
  });
  return heicDecoder;
}

async function decode(file, heicDecoderUrl) {
  try {
    return await createImageBitmap(file);
  } catch (error) {
    if (!(await isHeic(file))) throw error;

    try {
      const heicTo = await loadHeicDecoder(heicDecoderUrl);
      return await heicTo({ blob: file, type: "bitmap" });
    } catch (cause) {
      throw new HeicConversionError(`Couldn't convert ${file.name} to JPEG`, { cause });
    }
  }
}

export async function compressImage(file, { heicDecoderUrl } = {}) {
  if (!file.type.startsWith("image/") && !(await isHeic(file))) return file;
  if (file.type === "image/gif" && file.size <= MAX_SIZE) return file;

  const bitmap = await decode(file, heicDecoderUrl);
  let { width, height } = scaledDimensions(bitmap.width, bitmap.height, MAX_DIMENSION);

  while (true) {
    let quality = INITIAL_QUALITY;

    while (quality >= MIN_QUALITY) {
      const blob = await compressToJpeg(bitmap, width, height, quality);
      if (blob.size <= MAX_SIZE) {
        return new File([blob], jpegFileName(file.name), {
          type: "image/jpeg",
        });
      }
      quality -= QUALITY_STEP;
    }

    width = Math.round(width / 2);
    height = Math.round(height / 2);
  }
}
